/**
 * Firebase Configuration
 * SECURITY: All sensitive credentials are now stored in environment variables
 * Following OWASP best practices for secure configuration management
 * https://cheatsheetseries.owasp.org/cheatsheets/Secrets_Management_Cheat_Sheet.html
 */

import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getAnalytics } from 'firebase/analytics';

/**
 * Validate that all required environment variables are present
 * Fail fast if configuration is incomplete
 */
const validateConfig = () => {
  const required = [
    'REACT_APP_FIREBASE_API_KEY',
    'REACT_APP_FIREBASE_AUTH_DOMAIN',
    'REACT_APP_FIREBASE_PROJECT_ID',
    'REACT_APP_FIREBASE_STORAGE_BUCKET',
    'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
    'REACT_APP_FIREBASE_APP_ID'
  ];
  
  const missing = required.filter(key => {
    const value = process.env[key];
    // Check if value exists and is not empty after trimming
    return !value || value.trim() === '';
  });
  
  if (missing.length > 0) {
    console.error('❌ Firebase Configuration Missing');
    console.error('Missing environment variables:', missing);
    console.error('\n📋 To fix this:');
    console.error('1. In Vercel Dashboard: Settings → Environment Variables');
    console.error('2. Add all REACT_APP_* variables');
    console.error('3. Set for Production environment');
    console.error('4. Redeploy the project');
    console.error('\n📁 Current environment variables loaded:');
    required.forEach(key => {
      const value = process.env[key];
      const isSet = value && value.trim() !== '';
      console.error(`   ${key}: ${isSet ? '✅ Set (' + value.substring(0, 20) + '...)' : '❌ Missing'}`);
    });
    return false;
  }
  console.log('✅ Firebase configuration loaded successfully');
  return true;
};

// Validate configuration before initializing
const isConfigValid = validateConfig();

/**
 * Firebase configuration object
 * All values are loaded from environment variables
 * NEVER hardcode credentials in source code
 */
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID // Optional
};

/**
 * Security Note: Firebase API keys are safe to expose in client-side code
 * They are not secret keys - they identify your Firebase project
 * Security is enforced through Firebase Security Rules on the backend
 * 
 * However, we still use environment variables for:
 * 1. Easy configuration management across environments
 * 2. Ability to rotate keys without code changes
 * 3. Following security best practices
 */

// Initialize Firebase
let app;
let auth;
let db;
let analytics;

if (isConfigValid) {
  try {
    app = initializeApp(firebaseConfig);
    
    // Initialize Firebase Authentication
    auth = getAuth(app);
    
    // Initialize Cloud Firestore
    db = getFirestore(app);
    
    // Initialize Analytics (optional, only in production)
    if (process.env.NODE_ENV === 'production' && firebaseConfig.measurementId) {
      analytics = getAnalytics(app);
    }
    
    // Firebase initialized successfully
  } catch (error) {
    console.error('Error initializing Firebase:', error);
    console.error('Please check your Firebase configuration and ensure all credentials are correct.');
  }
}

// Export initialized services
export { auth, db, analytics };
export default app;
